import { listPublicPlans } from '@wix/ambassador-pricing-plans-v2-plan/http';
import { PublicPlan } from '@wix/ambassador-pricing-plans-v2-plan/types';
import { plansPageView } from '@wix/bi-logger-pricing-plans-data/v2';
import { PlanListWidgetRole } from '@wix/pricing-plans-common/blocks';
import { TPA_EXPERIMENTS } from '@wix/pricing-plans-common/experiments';
import { hasSetupFee, isRecurringPlan } from '@wix/pricing-plans-utils';
import { demoImages, plansFixtureTranslated } from '../../fixtures';
import { uncompressUuidArray } from '../../services/uuid-compression';
import { WarmupData } from '../../services/WarmupData';
import { PlanListBlocksInteraction } from '../../types/PlanListBlocksFedops';
import { BiCheckoutVersion, isViewerOnEcom } from '../../utils/ecom';
import { toError } from '../../utils/errors';
import { getGlobalObject } from '../../utils/get-global-object';
import { getHostEditorType } from '../../utils/host-environment';
import { retry401 } from '../../utils/retry401';
import { CtaClickHandler, PlanElementOptions } from '../Plan/viewer.controller';
import model, { PlanListWidgetProps } from './model';

export type PlanVariantState = Extract<
  PlanListWidgetRole,
  | PlanListWidgetRole.PlanVariantDefaultState
  | PlanListWidgetRole.PlanVariantHighlightedState
  | PlanListWidgetRole.PlanVariantCustomState
>;

type PlanVariantRole = Extract<
  PlanListWidgetRole,
  | PlanListWidgetRole.PlanVariantDefaultWidget
  | PlanListWidgetRole.PlanVariantHighlightedWidget
  | PlanListWidgetRole.PlanVariantCustomWidget
>;

type PlanListWidgetState = Extract<
  PlanListWidgetRole,
  | PlanListWidgetRole.EmptyState
  | PlanListWidgetRole.PlansInfoState
  | PlanListWidgetRole.LoadingState
  | PlanListWidgetRole.ErrorState
>;

interface RepeaterItemData {
  _id: string;
  plan: PublicPlan;
}

const PLAN_VARIANT_STATE_TO_WIDGET_VARIANT_ID: { [key in PlanVariantState]: `#${PlanVariantRole}` } = {
  [PlanListWidgetRole.PlanVariantDefaultState]: `#${PlanListWidgetRole.PlanVariantDefaultWidget}`,
  [PlanListWidgetRole.PlanVariantHighlightedState]: `#${PlanListWidgetRole.PlanVariantHighlightedWidget}`,
  [PlanListWidgetRole.PlanVariantCustomState]: `#${PlanListWidgetRole.PlanVariantCustomWidget}`,
};

export default model.createController(({ $w, $widget, flowAPI, controllerConfig }) => {
  const elementOptions: PlanElementOptions = {
    defaultCoverImage: demoImages.default,
  };
  const warmUpData = new WarmupData(controllerConfig.compId, controllerConfig.wixCodeApi, flowAPI);

  let externalOnSelectHandler: CtaClickHandler | undefined;
  let autoLoadPlans = true;
  let isOnEcom: boolean;

  const getDemoPlansIfEditor = () => {
    return flowAPI.environment.isEditor ? plansFixtureTranslated(flowAPI.translations.t, getHostEditorType()) : null;
  };

  const getOrderedPlans = async (planIds?: string[]) => {
    if (!planIds?.length) {
      return getDemoPlansIfEditor();
    }

    const response = await retry401(controllerConfig.wixCodeApi, flowAPI, () => fetchPlans(planIds));

    return response.data.plans
      ? response.data.plans.sort((a, b) => (planIds.indexOf(a.id!) > planIds.indexOf(b.id!) ? 1 : -1))
      : getDemoPlansIfEditor();
  };

  const fetchPlans = async (planIds?: string[]) => {
    const cacheKey = await getCacheKey();
    const isErrorHandlerEnabled = flowAPI.experiments.enabled(TPA_EXPERIMENTS.ERROR_HANDLER_TPA);

    const listPlans = () => {
      if (isErrorHandlerEnabled) {
        return flowAPI.errorHandler.withErrorHandler(() => flowAPI.httpClient.request(listPublicPlans({ planIds })), {
          errorCodesMap: { applicationError: { PRICING_PLANS_NOT_INSTALLED: false } },
        });
      } else {
        return flowAPI.httpClient.request(listPublicPlans({ planIds }));
      }
    };

    return cacheKey ? warmUpData.cache(cacheKey, () => listPlans()) : listPlans();
  };

  const getCacheKey = async () => {
    const planIds = $widget.props.planIds;
    const planIdsHashed = await hashString(planIds);
    if (planIdsHashed) {
      return `plan-list-${planIdsHashed}`;
    }

    return null;
  };

  const showState = (state: PlanListWidgetState) => {
    $w(`#${PlanListWidgetRole.PlanListStates}`).changeState(state);
  };

  const setErrorMessage = (message: string) => {
    $w(`#${PlanListWidgetRole.ErrorStateSubtitle}`).text = message;
  };

  const setWidgetData = (
    plans: PublicPlan[],
    props: Pick<PlanListWidgetProps, 'customStylePlanIds' | 'highlightedPlanIds'>,
  ) => {
    setPricingElementOptions(plans);
    const highlightedPlanIds = getUncompressedPlanIds(props.highlightedPlanIds);
    const customStylePlanIds = getUncompressedPlanIds(props.customStylePlanIds);

    $w(`#${PlanListWidgetRole.PlanList}`).onItemReady(($item, { plan }: RepeaterItemData) =>
      updatePlanItem($item, { plan, highlightedPlanIds, customStylePlanIds }),
    );

    $w(`#${PlanListWidgetRole.PlanList}`).data = plans.map(
      (plan) =>
        ({
          _id: plan.id!,
          plan,
        } satisfies RepeaterItemData),
    );

    showState(PlanListWidgetRole.PlansInfoState);
  };

  const updateDisplayedPlans = async (newProps: PlanListWidgetProps) => {
    try {
      const planIds = getUncompressedPlanIds(newProps.planIds);
      const plans = await getOrderedPlans(planIds);
      await initializeRepeaterItems(planIds);
      if (plans) {
        setWidgetData(plans, newProps);
      }
    } catch (e) {
      if (flowAPI.experiments.enabled(TPA_EXPERIMENTS.ERROR_HANDLER_TPA)) {
        flowAPI.panoramaClient?.errorMonitor().reportError(toError(e));
        const error = flowAPI.errorHandler.getResolvedError(e);
        setErrorMessage(error.message);
        showState(PlanListWidgetRole.ErrorState);
      } else {
        throw e;
      }
    }
  };

  const updatePlanStyleVariants = async (newProps: PlanListWidgetProps) => {
    const highlightedPlanIds = getUncompressedPlanIds(newProps.highlightedPlanIds);
    const customStylePlanIds = getUncompressedPlanIds(newProps.customStylePlanIds);
    $w(`#${PlanListWidgetRole.PlanList}`).forEachItem(($item, { plan }: RepeaterItemData) =>
      updatePlanItem($item, { plan, highlightedPlanIds, customStylePlanIds }),
    );
  };

  $widget.onPropsChanged((oldProps, newProps) => {
    if (hasDisplayedPlansChanged(oldProps, newProps)) {
      updateDisplayedPlans(newProps);
    } else if (hasPlanStyleVariantChanged(oldProps, newProps) || hasPlanCardOrientationChanged(oldProps, newProps)) {
      updatePlanStyleVariants(newProps);
    }
  });

  const updatePlanItem = async (
    $item: $w.$w,
    data: {
      plan: PublicPlan;
      highlightedPlanIds?: string[];
      customStylePlanIds?: string[];
    },
  ) => {
    const { plan, highlightedPlanIds, customStylePlanIds } = data;
    if (!plan) {
      return;
    }
    const state = getPlanWidgetState({ planId: plan.id!, highlightedPlanIds, customStylePlanIds });
    $item(`#${PlanListWidgetRole.PlanVariantBox}`).changeState(state);
    const planId = PLAN_VARIANT_STATE_TO_WIDGET_VARIANT_ID[state];
    $item(planId).setPlan(plan, getElementOptions());
    $item(planId).registerCtaHandler(plan, 'list_blocks', await getIsOnEcom());
    if (externalOnSelectHandler) {
      $item(planId).onSelect(externalOnSelectHandler);
    }
  };

  const initializeRepeaterItems = (planIds: string[] | undefined) => {
    if (!planIds?.length) {
      return;
    }
    /*
      Set plan ids on repeater to initialize repeater items and call
      `pageReady` on inner widgets. This will prevent an issue, where
      repeater's `onItemReady` is triggered before `pageReady` in inner widget
    */
    return new Promise((resolve) => {
      $w(`#${PlanListWidgetRole.PlanList}`).onItemReady(resolve);
      $w(`#${PlanListWidgetRole.PlanList}`).data = planIds?.map((id) => ({ _id: id })) ?? [];
    });
  };

  const configureErrorState = (planIds?: string[]) => {
    $w(`#${PlanListWidgetRole.ErrorStateButton}`).onClick(() => fetchAndSetPlans(planIds));
    $w(`#${PlanListWidgetRole.ErrorStateTitle}`).text = flowAPI.translations.t('blocks.plan-list.error-state.title');
    $w(`#${PlanListWidgetRole.ErrorStateSubtitle}`).text = flowAPI.translations.t(
      'blocks.plan-list.error-state.subtitle',
    );
    $w(`#${PlanListWidgetRole.ErrorStateButton}`).label = flowAPI.translations.t(
      'blocks.plan-list.error-state.button-label',
    );
  };

  const configureEmptyState = () => {
    $w(`#${PlanListWidgetRole.EmptyStateTitle}`).text = flowAPI.translations.t('blocks.plan-list.empty-state.title');
    $w(`#${PlanListWidgetRole.EmptyStateSubtitle}`).text = flowAPI.translations.t(
      'blocks.plan-list.empty-state.subtitle',
    );
  };

  const fetchAndSetPlans = async (planIds?: string[]) => {
    flowAPI.panoramaClient?.transaction(PlanListBlocksInteraction.FetchAndSetPlans).start();
    try {
      await initializeRepeaterItems(planIds);
      const plans = await getOrderedPlans(planIds);
      if (!plans?.length) {
        return showState(PlanListWidgetRole.EmptyState);
      }
      setWidgetData(plans, $widget.props);
      reportWidgetLoaded(await getIsOnEcom());
      flowAPI.panoramaClient?.transaction(PlanListBlocksInteraction.FetchAndSetPlans).finish();
    } catch (e) {
      if (flowAPI.experiments.enabled(TPA_EXPERIMENTS.ERROR_HANDLER_TPA)) {
        const error = flowAPI.errorHandler.getResolvedError(e);
        setErrorMessage(error.message);
      }

      showState(PlanListWidgetRole.ErrorState);
      flowAPI.panoramaClient?.errorMonitor().reportError(toError(e));
    }
  };

  const setPricingElementOptions = (plans: PublicPlan[]) => {
    elementOptions.forceExpandFrequency = plans.some((plan) => isRecurringPlan(plan));
    elementOptions.forceExpandSetupFee = plans.some((plan) => hasSetupFee(plan));
    elementOptions.forceExpandFreeTrial = plans.some((plan) => Boolean(plan.pricing?.freeTrialDays));
  };

  const getElementOptions = (): PlanElementOptions => {
    const shouldExpandEmptyElements = !$widget.props.isHorizontal && !flowAPI.environment.isMobile;
    if (shouldExpandEmptyElements) {
      return elementOptions;
    }

    return {
      ...elementOptions,
      forceExpandFreeTrial: false,
      forceExpandFrequency: false,
      forceExpandSetupFee: false,
    };
  };

  const getIsOnEcom = async () => {
    if (isOnEcom === undefined) {
      isOnEcom = await isViewerOnEcom(
        flowAPI.httpClient,
        controllerConfig.wixCodeApi,
        controllerConfig.appParams,
        flowAPI.errorHandler,
      );
    }
    return isOnEcom;
  };

  const reportWidgetLoaded = async (isEcom: boolean) => {
    flowAPI.bi?.report(
      plansPageView({
        widgetType: 'list_blocks',
        checkoutVersion: isEcom ? BiCheckoutVersion.Ecom : BiCheckoutVersion.PricingPlans,
      }),
    );
  };

  return {
    pageReady: async () => {
      flowAPI.panoramaClient?.transaction(PlanListBlocksInteraction.PlanListPageReady).start();

      const isWidgetRendered = $w(`#${PlanListWidgetRole.PlanList}`).rendered;
      if (!autoLoadPlans || (flowAPI.environment.isEditor && !isWidgetRendered)) {
        return;
      }

      configureEmptyState();
      if (!flowAPI.environment.isEditor && !flowAPI.environment.isSSR) {
        showState(PlanListWidgetRole.LoadingState);
      }
      const planIds = getUncompressedPlanIds($widget.props.planIds);
      configureErrorState(planIds);
      await fetchAndSetPlans(planIds);

      flowAPI.panoramaClient?.transaction(PlanListBlocksInteraction.PlanListPageReady).finish();
    },
    exports: {
      onSelect: (cb: CtaClickHandler) => {
        externalOnSelectHandler = cb;
      },
      setPlans: async (planIds: string[]) => {
        autoLoadPlans = false;
        configureErrorState(planIds);
        fetchAndSetPlans(planIds);
      },
      setTitle: (title: string) => {
        $w(`#${PlanListWidgetRole.Title}`).text = title;
      },
      setSubtitle: (subtitle: string) => {
        $w(`#${PlanListWidgetRole.Subtitle}`).text = subtitle;
      },
    },
  };
});

function getUncompressedPlanIds(planIdsProp?: string): string[] | undefined {
  return planIdsProp ? uncompressUuidArray(planIdsProp.split(',')).filter((id) => id !== '') : undefined;
}

function getPlanWidgetState(params: {
  planId: string;
  highlightedPlanIds?: string[];
  customStylePlanIds?: string[];
}): PlanVariantState {
  const { planId, highlightedPlanIds, customStylePlanIds } = params;
  if (highlightedPlanIds?.includes(planId)) {
    return PlanListWidgetRole.PlanVariantHighlightedState;
  }

  if (customStylePlanIds?.includes(planId)) {
    return PlanListWidgetRole.PlanVariantCustomState;
  }

  return PlanListWidgetRole.PlanVariantDefaultState;
}

function hasDisplayedPlansChanged(oldProps: PlanListWidgetProps, newProps: PlanListWidgetProps): boolean {
  return oldProps.planIds !== newProps.planIds;
}

function hasPlanStyleVariantChanged(oldProps: PlanListWidgetProps, newProps: PlanListWidgetProps): boolean {
  return (
    oldProps.customStylePlanIds !== newProps.customStylePlanIds ||
    oldProps.highlightedPlanIds !== newProps.highlightedPlanIds
  );
}

function hasPlanCardOrientationChanged(oldProps: PlanListWidgetProps, newProps: PlanListWidgetProps): boolean {
  return oldProps.isHorizontal !== newProps.isHorizontal;
}

async function hashString(str: string): Promise<string | null> {
  try {
    const encoder = new TextEncoder();
    const data = encoder.encode(str);
    const globalObj = getGlobalObject();
    if (!globalObj) {
      return null;
    }

    const hashBuffer = await globalObj.crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
    return hashHex;
  } catch (e) {
    return null;
  }
}
